import clsx from 'clsx';
import { type ReactNode, useRef, useState } from 'react';

import type { StudyDocument } from './studyApi';
import { StudyMaterialActionsMenu } from './StudyMaterialActionsMenu';
import { StudyMaterialAnalyseEntry } from './StudyMaterialAnalyseEntry';
import { StudyMaterialDialog } from './StudyMaterialDialog';
import { StudyMaterialRenameDialog } from './StudyMaterialRenameDialog';
import { StudyMaterialThumbnail } from './StudyMaterialThumbnail';
import { materialHeading, materialStatusWords } from './studyMaterialRow';

/**
 * One material in the Subject Materials tab and the Materials library (owner feedback
 * 2026-10-08: "the Open button is too big, the thumbnail is too small, and the name cannot be
 * renamed"). The whole card opens the material; there is no separate Open button. The face shows
 * the thumbnail, the title, the file name when it differs, and the analysis line. Rename, filing
 * and Delete live in the "⋯" menu at the top-right; each opens a small dialog.
 */

export interface MaterialCardAction {
  id: string;
  label: string;
  danger?: boolean;
  /** Runs at once (no dialog). */
  onSelect?: () => void;
  /** Opens a small dialog with this title and body; `close` closes it. */
  dialog?: { title: string; body: (close: () => void) => ReactNode };
}

export function StudyMaterialCard({
  document,
  variant,
  meta,
  actions,
  headingLevel = 'h3',
  onOpen,
}: {
  document: StudyDocument;
  /** `row`: inside one bordered list (Subject tab). `card`: a separate card (library). */
  variant: 'row' | 'card';
  /** One small line under the title, such as "Mathematics · Term 4 2026". */
  meta?: string;
  /** Menu actions after Rename. */
  actions: MaterialCardAction[];
  headingLevel?: 'h2' | 'h3';
  onOpen: () => void;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const [renaming, setRenaming] = useState(false);
  // Keep the id, not the action: the dialog body must read this render's props (filing drafts).
  const [openDialogId, setOpenDialogId] = useState<string | null>(null);
  const openDialog = actions.find((action) => action.id === openDialogId) ?? null;
  const heading = materialHeading(document);
  // Only words the learner can act on; an automatic filing is undone from the "⋯" menu.
  const statusWords =
    materialStatusWords(document.status) ??
    (document.classification_state === 'auto_organised'
      ? 'Suggested from filename · confirm the subject or undo'
      : null);
  const Heading = headingLevel;
  const close = () => setOpenDialogId(null);

  const menuActions = [
    { id: 'rename', label: 'Rename', onSelect: () => setRenaming(true) },
    ...actions.map((action) => ({
      id: action.id,
      label: action.label,
      danger: action.danger,
      onSelect: () => {
        if (action.dialog) {
          setOpenDialogId(action.id);
          return;
        }
        trigger.current?.focus();
        action.onSelect?.();
      },
    })),
  ];

  return (
    <article
      data-testid="study-material-card"
      className={clsx(
        'study-material-card group relative flex min-w-0 items-start gap-3 p-4 transition sm:gap-4 sm:p-5',
        variant === 'card'
          ? 'rounded-3xl border border-hairline bg-canvas-pure shadow-card-soft hover:border-brand-sky/50'
          : 'hover:bg-surface',
      )}
    >
      {/* One button covers the card: thumbnail and title area open the material. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${heading.title}`}
        className={clsx(
          'absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-sky',
          variant === 'card' && 'rounded-3xl',
        )}
      />
      <StudyMaterialThumbnail
        documentId={document.id}
        mimeType={document.mime_type}
        thumbnail={document.thumbnail}
      />
      <div className="min-w-0 flex-1 pr-10 pt-1">
        <Heading className="line-clamp-2 break-words text-base font-extrabold text-ink group-hover:text-brand-sky sm:text-lg">
          {heading.title}
        </Heading>
        {heading.subtitle && (
          <p className="truncate text-xs font-semibold text-slate2">{heading.subtitle}</p>
        )}
        {meta && <p className="mt-1 truncate text-xs font-semibold text-slate2">{meta}</p>}
        {statusWords && <p className="mt-1 text-xs font-bold text-slate2">{statusWords}</p>}
        <div className="relative z-10 mt-1 w-fit max-w-full">
          <StudyMaterialAnalyseEntry document={document} title={heading.title} />
        </div>
      </div>
      <div className="absolute right-2 top-2 z-20">
        <StudyMaterialActionsMenu
          title={heading.title}
          actions={menuActions}
          triggerRef={trigger}
        />
      </div>
      {renaming && (
        <StudyMaterialRenameDialog
          documentId={document.id}
          currentTitle={heading.title}
          hasChosenName={Boolean(document.display_name?.trim())}
          returnFocusRef={trigger}
          onClose={() => setRenaming(false)}
        />
      )}
      {openDialog?.dialog && (
        <StudyMaterialDialog
          title={openDialog.dialog.title}
          onClose={close}
          returnFocusRef={trigger}
        >
          {openDialog.dialog.body(close)}
        </StudyMaterialDialog>
      )}
    </article>
  );
}
